// ELS COLORS DELS LLENÇOS, en un sol lloc.
//
// Les pantalles normals van amb classes de Tailwind i el tema el governa `tailwind.config.js`.
// Els llenços, no: dibuixen amb SVG i amb `canvas`, on no hi ha classes, i fins al 25/08/2026
// hi havia **47 hexadecimals escrits a mà** repartits per quatre fitxers. Mentre hi fossin, cap
// canvi de tema arribava al dibuix — i el dibuix és la meitat d'aquesta aplicació.
//
// ── El tema: ORDIT I TRAMA ──────────────────────────────────────────────────────────────────
//
// El segon color **vol dir alguna cosa**, i és l'única regla que cal recordar d'aquí:
//
//     blau   = ESTOC     el que s'acumula i es queda
//     terra  = FLUX      el que es mou i travessa
//
// Abans el llenç tenia blau per als estocs, verd per als fluxos, porpra per als convertidors i
// ambre per als núvols: quatre colors triats per distingir-los, no per dir res. Amb dos colors
// que signifiquen, la resta de peces han d'anar a un neutre — si tot té color, cap color avisa.
//
// Els valors quadren amb els de `tailwind.config.js`: `primary-800` és l'estoc i `flux-700` el
// flux. Si allà canvien, aquí també.

const BLAU = { fort: '#1B2C55', mig: '#28417A', fluix: '#EDF0F7' };   // primary-900/800/50
const TERRA = { fort: '#7E3E15', mig: '#A8541E', fluix: '#FAF0E8' };  // flux-900/700/50
const NEUTRE = { fort: '#3A3832', mig: '#6B675E', fluix: '#FFFFFF', pal: '#A9A59B' };

// Cada peça del llenç: com s'omple, com es vora, i com es vora quan està seleccionada.
export const LLENC = {
  // Les dues que porten significat.
  estoc: { farciment: BLAU.fluix, vora: BLAU.mig, seleccio: BLAU.fort },
  flux: { farciment: TERRA.fluix, vora: TERRA.mig, seleccio: TERRA.fort },

  // La resta, al neutre. Un convertidor no és ni un estoc ni un flux, i pintar-lo d'un tercer
  // color inventat era el que feia que el color no volgués dir res.
  convertidor: { farciment: NEUTRE.fluix, vora: NEUTRE.mig, seleccio: NEUTRE.fort },
  nuvol: { farciment: NEUTRE.fluix, vora: NEUTRE.pal, seleccio: NEUTRE.mig },
  connector: { farciment: NEUTRE.fluix, vora: NEUTRE.mig, seleccio: NEUTRE.fort },

  // Bucles causals: les variables són nodes, i el node és el que s'acumula → blau.
  variable: { farciment: BLAU.fluix, vora: BLAU.mig, seleccio: BLAU.fort },

  // Estructura del llenç.
  reixa: '#E2E0DA',
  fons: '#FFFFFF',
  text: '#191A1D',
  textPal: NEUTRE.mig,
  linia: NEUTRE.mig,
  enllacDestacat: BLAU.mig,
  enCurs: TERRA.mig,          // mentre s'està traçant un enllaç
};

// Polaritat dels enllaços causals. Verd i vermell perquè és la convenció de la disciplina i
// canviar-la confondria qui ja la coneix. El vermell tira a fosc a posta: un vermell viu al
// costat del terra dels fluxos es confondrien, encara que avui no comparteixin pantalla.
export const POLARITAT = { positiva: '#2F6A4F', negativa: '#9E2B2B' };

// ELS COLORS DE LES VARIABLES.
//
// Quatre, i el número surt d'un càlcul, no d'un gust. Es va buscar la paleta que maximitza la
// distància perceptiva mínima entre parelles **també simulant els dos daltonismes més comuns**,
// amb dues condicions: prou fosca per portar text blanc a sobre (≥ 4,5:1) i prou apagada per no
// convertir el llenç en un semàfor.
//
// El resultat va ser tossut: amb aquestes condicions **només quatre colors es distingeixen de
// debò**. Amb cinc la parella més propera baixa a ΔE 19,5 i amb sis a 17,6 —per sota de 20 es
// confonen—. I les combinacions «harmòniques» fetes a ull encara pitjor: el blau i el terracota
// del tema, que junts queden preciosos, donen **ΔE 11 sota deuteranopia**, gairebé el mateix
// color per a qui la té.
//
// Per això el gràfic no es fia només del color: hi afegeix el **traç** (continu, ratllat, puntejat)
// com a segon canal. Quatre colors per tres traços són dotze sèries distingibles sense inventar
// ni un to més.
//
//   ΔE mínim entre parelles: 33,5 normal · 22,1 deuteranopia · 24,0 protanopia
export const PALETA_VARIABLES = [
  '#28417A',  // blau — l'accent del tema
  '#916F30',  // ocre
  '#315F2F',  // verd
  '#795292',  // violeta apagat
];

// El segon canal del gràfic.
export const TRACOS = ['', '6 3', '2 3'];

export const colorPerIndex = (i) => PALETA_VARIABLES[i % PALETA_VARIABLES.length];
export const tracPerIndex = (i) => TRACOS[Math.floor(i / PALETA_VARIABLES.length) % TRACOS.length];

// Els colors que una persona pot triar per a un component. Vuit, tots del mateix món que el
// tema: la llista d'abans era el porpra, el rosa i el turquesa que venien de la plantilla.
// Els mateixos quatre, més el neutre per a les variables que no s'han de destacar. Abans n'hi
// havia vuit triats a ull, i quatre d'ells es confonien entre si al gràfic.
export const TRIABLES = [
  { nom: 'Blue', valor: PALETA_VARIABLES[0] },
  { nom: 'Ochre', valor: PALETA_VARIABLES[1] },
  { nom: 'Green', valor: PALETA_VARIABLES[2] },
  { nom: 'Violet', valor: PALETA_VARIABLES[3] },
  { nom: 'Grey', valor: NEUTRE.mig },
];

// ELS SÍMBOLS DE LA IMATGE RICA.
//
// Una imatge rica és desordenada a posta, però **el color hi ha de voler dir el mateix que a la
// resta de l'aplicació** o deixa de dir res. Es reaprofiten els dos significats del tema:
//
//     blau  = el que s'acumula i es queda  →  les estructures
//     terra = el que es mou i travessa     →  els processos
//
// La gent va al neutre —ni s'acumula ni travessa, hi és—, i les preocupacions i el clima fan
// servir el verd i el vermell de la polaritat, que ja volen dir «va bé» i «va malament».
//
// **El farciment és el mateix to molt clar i el traç és el to fort**: així el símbol té cos i es
// distingeix de lluny, que és el que ha de passar en un dibuix que es mira en grup.
export const SIMBOLS = {
  actor: { traç: NEUTRE.fort, farciment: '#F2F1EE' },
  structure: { traç: BLAU.mig, farciment: BLAU.fluix },
  process: { traç: TERRA.mig, farciment: TERRA.fluix },
  concern: { traç: POLARITAT.negativa, farciment: '#FBEDED' },
  climate_good: { traç: POLARITAT.positiva, farciment: '#EDF5F1' },
  climate_bad: { traç: POLARITAT.negativa, farciment: '#FBEDED' },
  bubble: { traç: NEUTRE.mig, farciment: '#FFFFFF' },
  arrow: { traç: NEUTRE.fort, farciment: 'transparent' },
};

// ELS COLORS DEL LLAPIS de la imatge rica.
//
// El primer és el que ve per defecte i és **negre de debò**, no el gris de la reixa: fins al
// 27/08/2026 el traç heretava el color que `drawGrid` deixava posat i sortia gairebé invisible.
//
// Els altres cinc són els que ja tenen significat a l'aplicació —el blau del que s'acumula, el
// terra del que passa, el verd i el vermell de la polaritat— més un violeta per marcar sense dir
// res. En una imatge rica el color el tria qui dibuixa i no ha de voler dir res concret; el que
// importa és que **es distingeixin entre ells i sobre el blanc**, i aquests ja estan comprovats.
export const LLAPIS = [
  { nom: 'ink', valor: '#191A1D' },
  { nom: 'blue', valor: BLAU.mig },
  { nom: 'earth', valor: TERRA.mig },
  { nom: 'green', valor: '#2F6A4F' },
  { nom: 'red', valor: '#9E2B2B' },
  { nom: 'violet', valor: '#795292' },
];

// Els gruixos. **El de per defecte és el del mig i és 4**, no 2: una imatge rica es mira en grup
// i de lluny, i un traç de dos píxels no s'hi veu.
export const GRUIXOS_LLAPIS = [2, 4, 8];

export const PER_DEFECTE = BLAU.mig;

export default LLENC;
